import React, { useEffect, useRef } from 'react';
import { Button } from '../ui';
import type { AuthErrorInfo } from '../../helpers/auth_errors';

interface AuthErrorScreenProps {
  errorInfo: AuthErrorInfo;
  /** Explicit, user-initiated retry only — never called automatically. */
  onRetry: () => void;
  /** Clears the local Auth0 session/cache and returns to a clean, logged-out state. */
  onReset: () => void;
}

/**
 * Full-page takeover shown whenever Auth0 reports an error (wrong SSO
 * config, a blocked/denied account, a lockout, or a broken token exchange).
 *
 * This intentionally is NOT an overlay/modal on top of the app shell —
 * `App.tsx` renders this in place of the Sidebar + routed app entirely, so
 * a user in a broken auth state never sees any navigation chrome or
 * previously-cached page content behind it.
 */
const AuthErrorScreen: React.FC<AuthErrorScreenProps> = ({ errorInfo, onRetry, onReset }) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the heading so screen reader users landing here (e.g.
  // straight off an Auth0 redirect) immediately hear what happened.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      role="alert"
      className="min-h-screen w-full flex items-center justify-center bg-au-body-updated px-4"
    >
      <div className="w-full max-w-sm text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-au-danger-lightest mb-4">
          <svg
            className="w-8 h-8 text-au-danger"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.7-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
            />
          </svg>
        </div>

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold text-au-text-primary mb-3 outline-none"
        >
          {errorInfo.title}
        </h1>

        <p className="text-au-text-secondary mb-3">{errorInfo.reason}</p>

        {errorInfo.technicalDetail && (
          <p className="text-xs text-au-text-tertiary bg-au-surface-sunken rounded-au-base p-2 mb-2">
            {errorInfo.technicalDetail}
          </p>
        )}

        {errorInfo.isTokenExchangeFailure && (
          <p className="text-xs text-au-text-tertiary mt-1">
            We won&apos;t automatically retry signing you in, to avoid a redirect loop. Resetting will clear
            your local session so you can start fresh.
          </p>
        )}

        <div className="mt-6">
          {errorInfo.isTokenExchangeFailure ? (
            <Button variant="danger" onClick={onReset} fullWidth>
              Reset &amp; Return to Login
            </Button>
          ) : (
            <Button variant="danger" onClick={onRetry} fullWidth>
              Try Again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthErrorScreen;
